import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EmailIcon from '@mui/icons-material/Email';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { formatPriceWithCurrency } from '../../utils/vat';

// Map event types to icons and colors
const eventConfig = {
  created: { icon: AddIcon, color: 'success' },
  status_changed: { icon: SwapHorizIcon, color: 'info' },
  details_updated: { icon: EditIcon, color: 'info' },
  line_added: { icon: AddIcon, color: 'success' },
  line_updated: { icon: EditIcon, color: 'info' },
  line_removed: { icon: DeleteIcon, color: 'error' },
  discount_applied: { icon: LocalOfferIcon, color: 'warning' },
  email_sent: { icon: EmailIcon, color: 'info' },
  einvoice_sent: { icon: ReceiptIcon, color: 'info' },
};

function InvoiceEventLogDialog({ open, handleClose, invoice }) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && invoice?.id) {
      fetchEvents();
    }
  }, [open, invoice?.id]);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/invoices/${invoice.id}/event_log/`);
      setEvents(response.data);
    } catch (err) {
      setError(err.response?.data?.error || t('Error loading event log'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const locale = i18n.language === 'fi' ? 'fi-FI' : 'en-US';
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Group events by date
  const groupEventsByDate = (events) => {
    const groups = {};
    events.forEach((event) => {
      const date = new Date(event.created_at);
      const locale = i18n.language === 'fi' ? 'fi-FI' : 'en-US';
      const dateKey = date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(event);
    });
    return groups;
  };

  const getEventIcon = (eventType) => {
    const config = eventConfig[eventType] || { icon: HistoryIcon, color: 'default' };
    return config;
  };

  // Generate translated description from event type and details
  const getEventDescription = (event) => {
    const { event_type, details } = event;

    switch (event_type) {
      case 'created':
        if (details?.total_incl_vat !== undefined) {
          return t('invoiceEventLog.invoiceCreated', {
            total: `${Number(details.total_incl_vat).toFixed(2)} €`,
            lines: details.line_count || 0,
          });
        }
        return t('invoiceEventLog.invoiceCreatedGeneric');

      case 'status_changed':
        if (details?.amili_action === 'freeze') {
          const endDate = details.end_date ? new Date(`${details.end_date}T00:00:00`) : null;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return endDate && endDate > today
            ? t('invoiceEventLog.amiliFrozen', { date: endDate.toLocaleDateString(i18n.language === 'fi' ? 'fi-FI' : 'en-US') })
            : t('invoiceEventLog.amiliFreezeEnded');
        }
        if (details?.amili_action === 'cancel') {
          return t('invoiceEventLog.amiliCancelled', { reason: details.reason || '' });
        }
        if (details?.amili_direct_payment) {
          const key = details.automatic === false
            ? 'invoiceEventLog.amiliDirectPaymentReportedManual'
            : 'invoiceEventLog.amiliDirectPaymentReported';
          return t(key, {
            amount: formatPriceWithCurrency(details.amount),
            date: new Date(details.paid_date).toLocaleDateString(i18n.language === 'fi' ? 'fi-FI' : 'en-US'),
          });
        }
        if (details?.old_status && details?.new_status) {
          const oldStatus = t(details.old_status);
          const newStatus = t(details.new_status);
          return t('invoiceEventLog.statusChanged', { from: oldStatus, to: newStatus });
        }
        return t('invoiceEventLog.statusChangedGeneric');

      case 'details_updated':
        if (details?.updated_fields?.length > 0) {
          return t('invoiceEventLog.detailsUpdated', {
            fields: details.updated_fields.join(', '),
          });
        }
        return t('invoiceEventLog.detailsUpdatedGeneric');

      case 'line_added':
        if (details?.description) {
          const total = details.total ? `${Number(details.total).toFixed(2)} €` : '';
          return t('invoiceEventLog.lineAdded', { description: details.description, total });
        }
        return t('invoiceEventLog.lineAddedGeneric');

      case 'line_updated':
        if (details?.new_values?.description) {
          return t('invoiceEventLog.lineUpdated', { description: details.new_values.description });
        }
        return t('invoiceEventLog.lineUpdatedGeneric');

      case 'line_removed':
        if (details?.description) {
          return t('invoiceEventLog.lineRemoved', { description: details.description });
        }
        return t('invoiceEventLog.lineRemovedGeneric');

      case 'discount_applied':
        if (details?.discount_percent !== undefined) {
          const oldTotal = details.old_total ? `${Number(details.old_total).toFixed(2)} €` : '';
          const newTotal = details.new_total ? `${Number(details.new_total).toFixed(2)} €` : '';
          return t('invoiceEventLog.discountApplied', {
            percent: details.discount_percent,
            oldTotal,
            newTotal,
          });
        }
        return t('invoiceEventLog.discountAppliedGeneric');

      case 'email_sent':
        if (details?.recipient) {
          return t('invoiceEventLog.emailSent', { recipient: details.recipient });
        }
        return t('invoiceEventLog.emailSentGeneric');

      case 'einvoice_sent':
        if (details?.recipient_address) {
          return t('invoiceEventLog.einvoiceSent', { address: details.recipient_address });
        }
        return t('invoiceEventLog.einvoiceSentGeneric');

      default:
        // Fallback to backend description
        return event.description || t('invoiceEventLog.unknownEvent');
    }
  };

  const renderEvent = (event) => {
    const config = getEventIcon(event.event_type);
    const IconComponent = config.icon;

    return (
      <Box
        key={event.id}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          py: 1.5,
          '&:not(:last-child)': {
            borderBottom: 1,
            borderColor: 'divider',
          },
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${config.color}.lighter`,
            flexShrink: 0,
          }}
        >
          <IconComponent
            sx={{
              fontSize: 18,
              color: `${config.color}.main`,
            }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {getEventDescription(event)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {formatDate(event.created_at)}
            </Typography>
            {event.user_name && (
              <>
                <Typography variant="caption" color="text.disabled">
                  •
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {event.user_name}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const groupedEvents = groupEventsByDate(events);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '12px', sm: '16px' },
          m: { xs: 2, sm: 4 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="primary" />
          <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
            {t('Invoice Event Log')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
            {error}
          </Alert>
        )}

        {!loading && !error && events.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">{t('No events')}</Typography>
          </Box>
        )}

        {!loading && !error && events.length > 0 && (
          <Box>
            {Object.entries(groupedEvents).map(([date, dateEvents], index) => (
              <Box key={date}>
                {index > 0 && <Divider sx={{ my: 2 }} />}
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    mb: 1,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                    letterSpacing: '0.5px',
                  }}
                >
                  {date}
                </Typography>
                {dateEvents.map(renderEvent)}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default InvoiceEventLogDialog;
